import { getCurrencyFormatter } from "@redotech/money/util";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import LinkExternalIcon from "@redotech/redo-icons/link-external-02.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import {
  getReclaimActivationSourceForEntrySource,
  reclaimActivationEntrySources,
} from "@redotech/redo-model/disputes/reclaim-access-settings";
import { getReclaimPartnerCodeIntakeState } from "@redotech/redo-model/disputes/reclaim-partner-code-intake";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoElevatedOnboarding } from "@redotech/redo-web/arbiter-components/elevated-onboarding/redo-elevated-onboarding";
import { RedoElevatedOnboardingHeroPanel } from "@redotech/redo-web/arbiter-components/elevated-onboarding/redo-elevated-onboarding-hero-panel";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useReclaimAccessDefaults } from "../../../finance/disputes/use-reclaim-access-defaults";
import { useSetupGuide } from "../../provider/use-setup-guide";
import { getProductConfig } from "../../registry/product-registry";
import type {
  CustomIntakeExperienceChrome,
  IntakeAdditionalStepConfig,
  IntakeAdditionalStepFooterChrome,
  IntakeAnswers,
  IntakeAuthorizationStepConfig,
  IntakeInfoStepConfig,
  IntakeQuestion,
  Product,
} from "../../registry/types";
import { AnimatedLogos } from "./animated-logos";
import { DecryptedText } from "./decrypted-text";
import * as css from "./intake-modal.module.css";
import { IntakeOption } from "./intake-option";
import {
  buildIntakeSteps,
  canUseRequestedIntakeStep,
  getIntakeProgressPercentage,
  getNextIntakeStepId,
  isIntakeContinueDisabled,
  resolveInitialIntakeStepId,
  SETUP_STEP_PARAM,
} from "./intake-steps";
import { allQuestionsAnswered, isAnswered } from "./intake-validation";
import { IntakeStepTransition } from "./step-transition";

export interface IntakeModalProps {
  product: Product;
  /** Submit handler — provider should pipe to submitMerchantSetupIntake RPC. */
  onSubmit: (answers: IntakeAnswers) => Promise<void>;
  onClose?: () => void;
  /** Uses the Concierge-style slide/fade transition between intake steps. */
  animateStepTransitions?: boolean;
}

// Custom intake hero illustrating the merchant onboarding moment.
// Same S3 bucket the rest of the onboarding-v2 assets live in.
const DEFAULT_HERO_IMAGE_URL =
  "https://redoapi-dev.s3.us-east-1.amazonaws.com/redo/static/onboarding-v2/merchant-onboarding-in-app.webp";

const EMPTY_QUESTIONS: IntakeQuestion[] = [];
const EMPTY_ADDITIONAL_STEPS: IntakeAdditionalStepConfig[] = [];
const IS_LOCAL_DEVELOPMENT = process.env.NODE_ENV === "development";

// Same testimonials shown on the login hero panel — kept in sync manually
// for now since login is in a separate package.
const HERO_TESTIMONIALS = [
  {
    brand: "Willows Boutique",
    quote:
      "Auto batching has made our fulfillment process incredibly efficient. We're saving so much time!",
  },
  {
    brand: "Primo Golf",
    quote:
      "The cost savings is just nuts. We pay for dozens of point solutions and it adds up. Having all of your products under one platform is just so much better, and cheaper!",
  },
  {
    brand: "Popflex",
    quote:
      "Redo has transformed our returns process! The seamless ShipHero integration and customizable flows have saved our team countless hours, while the shop on-site exchange option keeps our customers happy and engaged.",
  },
  {
    brand: "Redo merchant",
    quote:
      "We were able to launch faster, test ideas sooner, and give our team one place to manage the customer journey.",
  },
  {
    brand: "Local Boy",
    quote:
      "Redo's software has made everything faster. It has freed up half of my day to work on other tasks.",
  },
  {
    brand: "Batch",
    quote:
      "The packing slips, the labels, our entire fulfillment setup, it all looks perfect. This setup is electric.",
  },
  {
    brand: "Henly",
    quote:
      "Redo's AI SMS abandonment messages are performing 3X better than our regular messages - that's huge for our brand! Super easy to set up and manage both SMS and email on the same page. Everything is just what I need!",
  },
  {
    brand: "ASRV",
    quote:
      "We appreciate Redo's seamless Shopify integration, robust reporting, and the ability to manage everything from returns to order tracking in one place. The team's responsiveness and commitment to our success make Redo an invaluable partner.",
  },
  {
    brand: "GLD",
    quote:
      "Honestly, Redo has been a game-changer for our exchange rates. We were looking for a way to get more customers to swap items instead of just returning them, and Redo has delivered.",
  },
  {
    brand: "Cuts",
    quote:
      "The cost savings alone made the change a no-brainer, but on top of that, their service has been unmatched.",
  },
] as const;

/**
 * Multi-step intake modal:
 *   • "welcome": "Welcome to Reclaim" + last-12-months chargeback loss stat
 *   • One step per intake question, prompt rendered as the H1 with no
 *     subtitle and the option list filling the body.
 *   • Optional final information and authorization steps before submit.
 *
 * Button text follows the position: "Continue" on welcome, "Next" between
 * questions/info steps, authorization CTA on authorization, "Get started" on
 * submit (or "Try again" after a submit error).
 *
 * Non-dismissible (intake submit failure must not strand the user — show
 * inline error + retry, never close on escape/backdrop).
 */
export const IntakeModal = memo(function IntakeModal({
  product,
  onSubmit,
  onClose,
  animateStepTransitions = false,
}: IntakeModalProps) {
  const config = getProductConfig(product);
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const setupGuide = useSetupGuide();
  const team = useContext(TeamContext);
  const location = useLocation();

  // Answers live on the provider so partial drafts survive the modal
  // unmounting + remounting when the merchant navigates away from the
  // product's routes (the host scopes modal rendering by route).
  const answers = setupGuide.intakeAnswers;
  const setAnswer = setupGuide.setIntakeAnswer;

  const [submitting, setSubmitting] = useState(false);
  const [authorizationPending, setAuthorizationPending] = useState(false);
  const [authorizationError, setAuthorizationError] = useState<string | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [customChrome, setCustomChrome] =
    useState<CustomIntakeExperienceChrome | null>(null);
  const [additionalStepFooterChrome, setAdditionalStepFooterChrome] = useState<{
    chrome: IntakeAdditionalStepFooterChrome;
    stepId: string;
  } | null>(null);

  // Pull last-12-months dispute stats (same source the legacy insights
  // page uses) so we can show "you're losing $X to chargebacks every year".
  const statsLoad = useLoad(
    async (signal) => {
      if (!config?.intake.welcome.useDisputeStatsSubtitle) {
        return undefined;
      }
      try {
        const today = Temporal.Now.plainDateISO();
        const result = await merchantRpcClient.getDisputeDashboardStats({
          startDate: today.subtract({ months: 12 }),
          endDate: today,
        });
        if (signal.aborted) {
          return undefined;
        }
        return result;
      } catch {
        return undefined;
      }
    },
    [config?.intake.welcome.useDisputeStatsSubtitle, merchantRpcClient],
  );
  const stats = statsLoad.value;
  const entrySource = setupGuide.activeProgress?.entrySource;
  const validatedEntrySource =
    entrySource &&
    (reclaimActivationEntrySources as readonly string[]).includes(entrySource)
      ? (entrySource as (typeof reclaimActivationEntrySources)[number])
      : undefined;
  // The intake modal IS the intake_modal source. If we have an explicit
  // recognized entry source, route through it (so insights_page_cta maps
  // to placeholder_landing, etc.); otherwise default to intake_modal so
  // displayed terms resolve against the right source rule rather than
  // silently falling back to global accessDefaults.
  const reclaimSource =
    product === "disputes"
      ? validatedEntrySource
        ? getReclaimActivationSourceForEntrySource(validatedEntrySource)
        : "intake_modal"
      : undefined;
  const { terms, termsCopy } = useReclaimAccessDefaults({
    enabled: product === "disputes",
    source: reclaimSource,
  });
  const partnerCodeState = getReclaimPartnerCodeIntakeState(answers);
  const displayedTermsCopy = useMemo(() => {
    if (product === "disputes" && partnerCodeState.status === "applied") {
      const partnerLabel = partnerCodeState.partnerName ?? "your partner";
      return {
        title: `Welcome from ${partnerLabel}`,
        body: "Your partner access is applied, so you can finish Reclaim setup without adding billing. Reclaim will defend your disputes automatically.",
      };
    }
    return termsCopy;
  }, [
    partnerCodeState.partnerName,
    partnerCodeState.status,
    product,
    termsCopy,
  ]);
  const currency = stats?.currency ?? "USD";
  const formatter = useMemo(() => getCurrencyFormatter(currency), [currency]);
  const annualLoss =
    stats && stats.totalCount > 0
      ? Math.max(0, stats.finalizedAmount - stats.wonAmount)
      : null;

  const questions = config?.intake.questions ?? EMPTY_QUESTIONS;
  const additionalStepContext = useMemo(
    () => ({ answers, reclaimTerms: terms, team }),
    [answers, team, terms],
  );
  const configuredAdditionalSteps =
    config?.intake.additionalSteps ?? EMPTY_ADDITIONAL_STEPS;
  const additionalSteps = useMemo(
    () =>
      configuredAdditionalSteps.filter((step) =>
        step.shouldInclude(additionalStepContext),
      ),
    [additionalStepContext, configuredAdditionalSteps],
  );
  const skippedAdditionalAnswers = useMemo(
    () =>
      configuredAdditionalSteps.reduce<IntakeAnswers>((nextAnswers, step) => {
        if (step.shouldInclude(additionalStepContext)) {
          return nextAnswers;
        }
        return {
          ...nextAnswers,
          ...step.getSkippedAnswers?.(additionalStepContext),
        };
      }, {}),
    [additionalStepContext, configuredAdditionalSteps],
  );
  const finalInfoStep = useMemo(() => {
    if (product !== "disputes") {
      return config?.intake.finalInfoStep ?? null;
    }
    return { title: displayedTermsCopy.title, body: displayedTermsCopy.body };
  }, [
    config?.intake.finalInfoStep,
    displayedTermsCopy.body,
    displayedTermsCopy.title,
    product,
  ]);
  const authorizationStep = config?.intake.authorizationStep;
  const requestedSetupStepId = useMemo(
    () => new URLSearchParams(location.search).get(SETUP_STEP_PARAM),
    [location.search],
  );
  const steps = useMemo(
    () =>
      buildIntakeSteps({
        additionalSteps,
        authorizationStep,
        finalInfoStep,
        isLocalDevelopment: IS_LOCAL_DEVELOPMENT,
        questions,
        team,
      }),
    [additionalSteps, authorizationStep, finalInfoStep, questions, team],
  );
  const effectiveRequestedSetupStepId = useMemo(
    () =>
      canUseRequestedIntakeStep({
        additionalSteps,
        answers,
        authorizationStepId: authorizationStep?.id,
        questions,
        requestedStepId: requestedSetupStepId,
        steps,
      })
        ? requestedSetupStepId
        : null,
    [
      additionalSteps,
      answers,
      authorizationStep?.id,
      questions,
      requestedSetupStepId,
      steps,
    ],
  );
  const appliedRequestedStepRef = useRef<string | null>(
    effectiveRequestedSetupStepId,
  );
  const [currentStepId, setCurrentStepId] = useState(() =>
    resolveInitialIntakeStepId({
      authorizationStepId: authorizationStep?.id,
      requestedStepId: effectiveRequestedSetupStepId,
      steps,
    }),
  );
  useEffect(() => {
    const hasCurrentStep = steps.some((step) => step.id === currentStepId);
    const requestedStepAlreadyApplied =
      !effectiveRequestedSetupStepId ||
      appliedRequestedStepRef.current === effectiveRequestedSetupStepId;
    if (
      hasCurrentStep &&
      requestedStepAlreadyApplied &&
      effectiveRequestedSetupStepId !== authorizationStep?.id
    ) {
      return;
    }
    const nextStepId = resolveInitialIntakeStepId({
      authorizationStepId: authorizationStep?.id,
      requestedStepId: effectiveRequestedSetupStepId,
      steps,
    });
    if (effectiveRequestedSetupStepId) {
      appliedRequestedStepRef.current = effectiveRequestedSetupStepId;
    }
    if (nextStepId !== currentStepId) {
      setCurrentStepId(nextStepId);
    }
  }, [
    authorizationStep?.id,
    currentStepId,
    effectiveRequestedSetupStepId,
    steps,
  ]);
  const currentStep =
    steps.find((step) => step.id === currentStepId) ?? steps[0];
  const lastRecordedStepRef = useRef<string | null>(null);
  useEffect(() => {
    if (!currentStep || lastRecordedStepRef.current === currentStep.id) {
      return;
    }
    lastRecordedStepRef.current = currentStep.id;
    setupGuide.recordIntakeStep(product, currentStep.id);
  }, [currentStep, product, setupGuide]);
  const allAnswered = !!config && allQuestionsAnswered(questions, answers);
  const CustomExperience = config?.intake.renderExperience;

  const currentQuestion =
    currentStep?.type === "question"
      ? questions[currentStep.questionIndex]
      : undefined;
  const currentAnswered =
    !!currentQuestion &&
    isAnswered(currentQuestion, answers[currentQuestion.id]);
  const nextStepId = currentStep
    ? getNextIntakeStepId(steps, currentStep.id)
    : null;
  const isLastStep = nextStepId == null;
  const currentAdditionalStep =
    currentStep?.type === "additional"
      ? additionalSteps[currentStep.additionalStepIndex]
      : undefined;
  const currentAdditionalStepComplete =
    currentStep?.type === "additional" &&
    currentAdditionalStep?.isComplete?.(answers) === true;
  const CurrentAdditionalStep = currentAdditionalStep?.render;
  const currentAdditionalStepFooterChrome =
    currentStep?.type === "additional" &&
    additionalStepFooterChrome?.stepId === currentStep.id
      ? additionalStepFooterChrome.chrome
      : null;
  const authorizationComplete =
    !!team && authorizationStep?.completionPredicate(team) === true;
  const showReturnedMissingPermissions =
    currentStep?.type === "authorization" &&
    requestedSetupStepId === currentStep.authorizationStepId &&
    !authorizationComplete;
  const displayedAuthorizationError =
    authorizationError ??
    (showReturnedMissingPermissions && authorizationStep
      ? authorizationStep.errorCopy.missingAfterReturn
      : null);
  const canSkipAuthorizationInDevelopment =
    IS_LOCAL_DEVELOPMENT &&
    currentStep?.type === "authorization" &&
    !!authorizationStep?.developmentSkipCtaText;

  const submitAnswers = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({ ...skippedAdditionalAnswers, ...answers });
      // Provider's onSuccess refetches; intakeCompletedAt flips and the
      // modal naturally unmounts via the showIntake gate.
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not save your answers. Please try again.",
      );
      setSubmitting(false);
    }
  }, [answers, onSubmit, skippedAdditionalAnswers]);

  const handleAdditionalStepContinue = useCallback(() => {
    if (nextStepId) {
      setCurrentStepId(nextStepId);
      return;
    }
    if (allAnswered) {
      void submitAnswers();
    }
  }, [allAnswered, nextStepId, submitAnswers]);

  const handleAdditionalStepFooterChromeChange = useCallback(
    (chrome: IntakeAdditionalStepFooterChrome | null) => {
      const stepId = currentStep?.type === "additional" ? currentStep.id : null;
      setAdditionalStepFooterChrome(
        chrome && stepId ? { chrome, stepId } : null,
      );
    },
    [currentStep?.id, currentStep?.type],
  );

  const currentAdditionalStepProps = useMemo(
    () =>
      currentAdditionalStep
        ? {
            answers,
            onFooterChromeChange: handleAdditionalStepFooterChromeChange,
            onContinue: handleAdditionalStepContinue,
            rpcClient: merchantRpcClient,
            setAnswer,
            team,
          }
        : null,
    [
      answers,
      currentAdditionalStep,
      handleAdditionalStepFooterChromeChange,
      handleAdditionalStepContinue,
      merchantRpcClient,
      setAnswer,
      team,
    ],
  );

  const handleContinue = useCallback(async () => {
    if (!currentStep) {
      return;
    }
    if (currentStep.type === "additional") {
      if (currentAdditionalStepFooterChrome?.onContinue) {
        await currentAdditionalStepFooterChrome.onContinue();
        return;
      }
      if (!currentAdditionalStepComplete) {
        return;
      }
      handleAdditionalStepContinue();
      return;
    }
    if (currentStep.type === "authorization") {
      if (!team || !authorizationStep) {
        setAuthorizationError(
          authorizationStep?.errorCopy.urlError ??
            "Could not open authorization. Please try again.",
        );
        return;
      }
      setAuthorizationPending(true);
      setAuthorizationError(null);
      try {
        const authorizationUrl = await authorizationStep.getAuthorizationUrl({
          rpcClient: merchantRpcClient,
          team,
        });
        window.location.href = authorizationUrl;
      } catch {
        setAuthorizationError(authorizationStep.errorCopy.urlError);
        setAuthorizationPending(false);
      }
      return;
    }
    if (currentStep.type === "question" && !currentAnswered) {
      return;
    }
    if (currentStep.type === "finalInfo" && !allAnswered) {
      return;
    }
    if (nextStepId) {
      setCurrentStepId(nextStepId);
      return;
    }
    if (allAnswered) {
      await submitAnswers();
    }
  }, [
    allAnswered,
    authorizationStep,
    currentAdditionalStepComplete,
    currentAdditionalStepFooterChrome,
    currentAnswered,
    currentStep,
    handleAdditionalStepContinue,
    merchantRpcClient,
    nextStepId,
    submitAnswers,
    team,
  ]);

  const handleSkipAuthorization = useCallback(async () => {
    if (!canSkipAuthorizationInDevelopment) {
      return;
    }
    setAuthorizationError(null);
    if (nextStepId) {
      setCurrentStepId(nextStepId);
      return;
    }
    if (allAnswered) {
      await submitAnswers();
    }
  }, [
    allAnswered,
    canSkipAuthorizationInDevelopment,
    nextStepId,
    submitAnswers,
  ]);

  if (!config) {
    return null;
  }

  const defaultContinueDisabled = isIntakeContinueDisabled({
    allAnswered,
    authorizationPending,
    currentAdditionalStepComplete,
    currentAnswered,
    currentStep,
    submitting,
  });
  const continueDisabled =
    currentStep?.type === "additional" &&
    currentAdditionalStepFooterChrome?.continueDisabled != null
      ? currentAdditionalStepFooterChrome.continueDisabled
      : defaultContinueDisabled;
  const additionalStepContinuePending =
    currentStep?.type === "additional" &&
    currentAdditionalStepFooterChrome?.continuePending === true;
  const continueText =
    currentStep?.type === "additional" &&
    currentAdditionalStepFooterChrome?.continueText
      ? currentAdditionalStepFooterChrome.continueText
      : currentStep?.type === "welcome"
        ? "Continue"
        : currentStep?.type === "additional"
          ? "Continue"
          : currentStep?.type === "authorization" && authorizationStep
            ? authorizationStep.ctaText
            : isLastStep
              ? error
                ? "Try again"
                : "Get started"
              : "Next";
  const showExternalLinkIcon = currentStep?.type === "authorization";
  const dismissible = config.intake.dismissible === true;
  const heroImageUrl = config.intake.heroImageUrl ?? DEFAULT_HERO_IMAGE_URL;
  const showProgress = config.intake.showProgress ?? !CustomExperience;
  const progressPercent =
    showProgress && CustomExperience && customChrome
      ? customChrome.totalSteps > 0
        ? Math.round(
            ((customChrome.currentStepIndex + 1) / customChrome.totalSteps) *
              100,
          )
        : 0
      : showProgress
        ? getIntakeProgressPercentage(steps, currentStep?.id)
        : undefined;

  const footer = CustomExperience ? (
    customChrome ? (
      <Flex align="center" gap="md" justify="space-between" w="full">
        {customChrome.footerError ? (
          <Text fontSize="sm" textColor="error">
            {customChrome.footerError}
          </Text>
        ) : (
          <div />
        )}
        <Flex align="center" gap="sm">
          {customChrome.onBack ? (
            <RedoButton
              hierarchy="secondary"
              onClick={customChrome.onBack}
              size="lg"
              text={customChrome.backText ?? "Back"}
            />
          ) : null}
          <RedoButton
            disabled={customChrome.continueDisabled}
            hierarchy="primary"
            onClick={() => {
              void customChrome.onContinue();
            }}
            pending={customChrome.continuePending}
            size="lg"
            text={customChrome.continueText}
          />
        </Flex>
      </Flex>
    ) : null
  ) : currentAdditionalStep &&
    !currentAdditionalStep.secondaryFooterButton &&
    !currentAdditionalStepComplete &&
    !currentAdditionalStepFooterChrome ? null : (
    <>
      {currentStep?.type === "additional" &&
        currentAdditionalStep?.secondaryFooterButton &&
        !currentAdditionalStepComplete &&
        currentAdditionalStepProps && (
          <RedoButton
            disabled={
              submitting ||
              authorizationPending ||
              additionalStepContinuePending
            }
            hierarchy="secondary"
            onClick={() => {
              void currentAdditionalStep.secondaryFooterButton?.onClick(
                currentAdditionalStepProps,
              );
            }}
            size="lg"
            text={currentAdditionalStep.secondaryFooterButton.text}
          />
        )}
      {canSkipAuthorizationInDevelopment && authorizationStep && (
        <RedoButton
          disabled={submitting || authorizationPending}
          hierarchy="secondary"
          onClick={handleSkipAuthorization}
          size="lg"
          text={authorizationStep.developmentSkipCtaText}
        />
      )}
      <RedoButton
        disabled={continueDisabled}
        hierarchy="primary"
        onClick={() => {
          void handleContinue();
        }}
        pending={
          currentStep?.type === "additional"
            ? additionalStepContinuePending
            : submitting || authorizationPending
        }
        size="lg"
        text={continueText}
        {...(showExternalLinkIcon ? { IconLeading: LinkExternalIcon } : {})}
      />
    </>
  );

  return (
    <RedoElevatedOnboarding
      bodyScroll={!CustomExperience}
      dismissible={dismissible}
      footer={footer}
      onCloseRequested={onClose}
      productName={config.displayName}
      progressPercent={progressPercent}
      rightPanel={
        <RedoElevatedOnboardingHeroPanel imageUrl={heroImageUrl}>
          <HeroPanelContent />
        </RedoElevatedOnboardingHeroPanel>
      }
    >
      {CustomExperience ? (
        <CustomExperience
          onChromeChange={setCustomChrome}
          onClose={dismissible ? onClose : undefined}
          onSubmit={onSubmit}
          product={product}
        />
      ) : (
        <div className={css.stepInner}>
          <IntakeStepTransition
            enabled={animateStepTransitions}
            stepKey={currentStep?.id ?? "missing"}
          >
            {currentStep?.type === "welcome" ? (
              <div className={css.welcomeBlock}>
                <WelcomeContent
                  annualLoss={annualLoss}
                  formatter={formatter}
                  loading={statsLoad.pending}
                  subtitle={config.intake.welcome.subtitle}
                  title={config.intake.welcome.title}
                  useDisputeStatsSubtitle={
                    config.intake.welcome.useDisputeStatsSubtitle === true
                  }
                />
              </div>
            ) : CurrentAdditionalStep && currentAdditionalStepProps ? (
              <CurrentAdditionalStep {...currentAdditionalStepProps} />
            ) : currentStep?.type === "finalInfo" && finalInfoStep ? (
              <FinalInfoContent error={error} step={finalInfoStep} />
            ) : currentStep?.type === "authorization" && authorizationStep ? (
              <AuthorizationContent
                error={displayedAuthorizationError}
                step={authorizationStep}
              />
            ) : currentQuestion ? (
              <div className={css.questionsBlock}>
                <h1 className={css.title}>{currentQuestion.prompt}</h1>
                <div
                  className={
                    currentQuestion.layout === "grid"
                      ? css.optionGrid
                      : css.optionList
                  }
                >
                  {currentQuestion.options.map((option) => (
                    <IntakeOption
                      disabled={submitting}
                      isSelected={answers[currentQuestion.id] === option.value}
                      key={option.value}
                      layout={currentQuestion.layout ?? "list"}
                      onClick={() =>
                        setAnswer(currentQuestion.id, option.value)
                      }
                      option={option}
                    />
                  ))}
                  {error && isLastStep && (
                    <Text fontSize="sm" textColor="error">
                      {error}
                    </Text>
                  )}
                </div>
              </div>
            ) : null}
          </IntakeStepTransition>
        </div>
      )}
    </RedoElevatedOnboarding>
  );
});

const WelcomeContent = memo(function WelcomeContent({
  annualLoss,
  formatter,
  loading,
  subtitle,
  title,
  useDisputeStatsSubtitle,
}: {
  annualLoss: number | null;
  formatter: Intl.NumberFormat;
  loading: boolean;
  subtitle: string;
  title: string;
  useDisputeStatsSubtitle: boolean;
}) {
  if (loading) {
    return null;
  }
  const resolvedSubtitle =
    useDisputeStatsSubtitle && annualLoss != null && annualLoss > 0
      ? `Looks like you're currently losing ${formatter.format(annualLoss)} to chargebacks every year. Let's fix that.`
      : subtitle;
  return (
    <>
      <h1 className={css.title}>
        <DecryptedText animateOn="view" sequential speed={40} text={title} />
      </h1>
      <p className={css.subtitle}>{resolvedSubtitle}</p>
    </>
  );
});

const FinalInfoContent = memo(function FinalInfoContent({
  error,
  step,
}: {
  error: string | null;
  step: IntakeInfoStepConfig;
}) {
  return (
    <div className={css.questionsBlock}>
      <h1 className={css.title}>{step.title}</h1>
      <Text fontSize="md" mt="lg" textColor="tertiary">
        {step.body}
      </Text>
      {error && (
        <Text fontSize="sm" mt="lg" textColor="error">
          {error}
        </Text>
      )}
    </div>
  );
});

const AuthorizationContent = memo(function AuthorizationContent({
  error,
  step,
}: {
  error: string | null;
  step: IntakeAuthorizationStepConfig;
}) {
  return (
    <div className={css.questionsBlock}>
      <h1 className={css.title}>{step.title}</h1>
      <Text fontSize="md" mt="lg" textColor="tertiary">
        {step.body}
      </Text>
      {error && (
        <Text fontSize="sm" mt="lg" textColor="error">
          {error}
        </Text>
      )}
    </div>
  );
});

const HeroPanelContent = memo(function HeroPanelContent() {
  const testimonial = useMemo(() => {
    const randomIndex = Math.floor(Math.random() * HERO_TESTIMONIALS.length);
    return HERO_TESTIMONIALS[randomIndex] ?? HERO_TESTIMONIALS[0];
  }, []);

  return (
    <>
      <div className={css.trustedBy}>
        <span className={css.trustedByLabel}>Trusted by leading brands</span>
        <AnimatedLogos />
      </div>
      <blockquote className={css.testimonial}>
        <p className={css.quote}>{`"${testimonial.quote}"`}</p>
        <footer className={css.attribution}>
          <span className={css.authorName}>{testimonial.brand}</span>
        </footer>
      </blockquote>
    </>
  );
});
